import { installPolyfills, setIframeSrcDoc } from 'polyfills/helpers';
import { MESSAGE_POLL_SETTINGS } from 'widget_main/app/constants';
import { CSP_NONCE_ALLOWED_URLS, getNonce } from 'helpers/nonce/helpers';

export const appendElementToDocument = (
  element: HTMLElement,
  callback?: () => void,
) => {
  const scriptEmbedElement = document.querySelector<HTMLScriptElement>(
    '[data-kustomer-api-key]',
  );

  if (!scriptEmbedElement || !scriptEmbedElement.parentNode) return;

  scriptEmbedElement.parentNode.insertBefore(element, scriptEmbedElement);
  callback?.();
};

/**
 * Applies CSP nonce to dynamically loaded scripts based on their src URL.
 *
 * This function intercepts various DOM manipulation methods to automatically add
 * CSP nonces to script elements that have src attributes matching the allowed URLs.
 * This is essential for CSP compliance when using strict Content Security Policies.
 *
 * The function works by:
 * 1. Overriding Node.prototype.appendChild and insertBefore to catch scripts added to DOM
 *    (including webpack chunk loads, which use these APIs — must run before any chunk injects)
 * 2. Overriding document.head / document.body appendChild and append when those nodes exist;
 *    body overrides are deferred to DOMContentLoaded if body is not parsed yet (avoids TypeError
 *    when the bundle runs in head before body exists).
 *
 * URL filtering logic:
 * - Scripts with no usable `src` (missing, empty, or whitespace-only) do **not** receive a
 *   nonce. Auto-noncing inline scripts would let any DOM-injected `<script>` execute under the
 *   page nonce; this SDK only loads **external** chunk/SDK scripts (all have `src`).
 * - External scripts: `src` must resolve to the same **origin** as an entry in
 *   `CSP_NONCE_ALLOWED_URLS` (not mere string prefix matching). Origin is derived with
 *   `<a href>` / `createHTMLDocument` + `<base>` so IE11 works before the URL polyfill loads.
 *
 * IMPORTANT: This function is idempotent - it will only execute once per page load,
 * regardless of how many times it's called. Subsequent calls will be ignored to
 * prevent DOM method override chains and memory leaks.
 *
 * @param nonce - The CSP nonce value to apply to matching scripts
 */
// Track whether the nonce application has already been initialized
// This ensures the function is idempotent and prevents multiple DOM method overrides
let areCSPOverridesApplied = false;

/** Nonce last applied by `applyNonceToDynamicScripts` — used to patch `append` after polyfills. */
let lastAppliedCspNonceForPatches: string | undefined;

// Export for testing purposes only
export const resetAreCSPOverridesAppliedState = () => {
  areCSPOverridesApplied = false;
  lastAppliedCspNonceForPatches = undefined;
};

/** Detect absolute URL (scheme:) without relying on the URL constructor (IE11-safe). */
const ABSOLUTE_URL_RE = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Normalized origin key (protocol + host + port) using anchor properties only.
 * IE11 has no URL API; this runs before installPolyfills().
 */
function originKeyFromAnchor(a: HTMLAnchorElement): string | null {
  const host = (a.hostname || '').toLowerCase();
  if (!host) {
    return null;
  }
  const proto = (a.protocol || '').replace(/:$/, '').toLowerCase();
  let port = String(a.port || '');
  if (
    (proto === 'http' && port === '80') ||
    (proto === 'https' && port === '443')
  ) {
    port = '';
  }
  return port ? `${proto}://${host}:${port}` : `${proto}://${host}`;
}

function resolveHrefWithBase(url: string, baseHref: string): string | null {
  try {
    const impl = document.implementation;
    if (!impl || typeof impl.createHTMLDocument !== 'function') {
      return null;
    }
    const doc = impl.createHTMLDocument('');
    const baseEl = doc.createElement('base');
    baseEl.setAttribute('href', baseHref);
    doc.head.appendChild(baseEl);
    const a = doc.createElement('a');
    a.setAttribute('href', url);
    return a.href || null;
  } catch {
    return null;
  }
}

function documentBaseForCsp(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return document.baseURI || document.URL || undefined;
}

function absoluteHrefForCspAllowlist(
  scriptSrc: string,
  baseUrl?: string,
): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const base = baseUrl ?? documentBaseForCsp();

  if (ABSOLUTE_URL_RE.test(scriptSrc)) {
    try {
      const a = document.createElement('a');
      a.setAttribute('href', scriptSrc);
      return a.href || null;
    } catch {
      return null;
    }
  }
  if (!base) {
    return null;
  }
  return resolveHrefWithBase(scriptSrc, base);
}

function originKeyFromAbsoluteHref(absoluteHref: string): string | null {
  try {
    const a = document.createElement('a');
    a.setAttribute('href', absoluteHref);
    return originKeyFromAnchor(a);
  } catch {
    return null;
  }
}

/** Fallback when `document` is missing (non-browser); uses WHATWG URL if available. */
function isScriptSrcAllowedForCspNonceWhatwg(
  trimmed: string,
  baseUrl?: string,
): boolean {
  if (typeof URL === 'undefined') {
    return false;
  }
  try {
    let candidate: URL | null = null;
    if (ABSOLUTE_URL_RE.test(trimmed)) {
      candidate = new URL(trimmed);
    } else if (baseUrl) {
      candidate = new URL(trimmed, baseUrl);
    }
    if (!candidate) {
      return false;
    }
    return CSP_NONCE_ALLOWED_URLS.some(pattern => {
      if (typeof pattern !== 'string') {
        return false;
      }
      try {
        return candidate.origin === new URL(pattern.trim()).origin;
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
}

/**
 * Whether a dynamic script `src` is allowed to receive a CSP nonce.
 * Missing or empty `src` is denied (do not bless attacker-controlled inline scripts).
 * External URLs must match an allowlist entry by **origin** (protocol + host + port).
 *
 * Uses `<a>` / `createHTMLDocument` + `<base>` so IE11 works before URL polyfills load.
 *
 * @param scriptSrc - value from `HTMLScriptElement.getAttribute('src')` or equivalent
 * @param baseUrl - optional base for relative `src` (defaults to `document.baseURI` in browser)
 */
export function isScriptSrcAllowedForCspNonce(
  scriptSrc?: string,
  baseUrl?: string,
): boolean {
  if (scriptSrc === undefined || scriptSrc === null || scriptSrc.trim() === '') {
    return false;
  }
  const trimmed = scriptSrc.trim();

  if (typeof document === 'undefined') {
    return isScriptSrcAllowedForCspNonceWhatwg(trimmed, baseUrl);
  }

  const candidateHref = absoluteHrefForCspAllowlist(trimmed, baseUrl);
  if (!candidateHref) {
    return false;
  }
  const candidateOrigin = originKeyFromAbsoluteHref(candidateHref);
  if (!candidateOrigin) {
    return false;
  }

  return CSP_NONCE_ALLOWED_URLS.some(pattern => {
    if (typeof pattern !== 'string') {
      return false;
    }
    const pat = document.createElement('a');
    pat.setAttribute('href', pattern.trim());
    const allowedOrigin = originKeyFromAnchor(pat);
    return allowedOrigin !== null && allowedOrigin === candidateOrigin;
  });
}

/**
 * Applies CSP nonce to a single dynamic script node when URL rules allow.
 * Used by DOM overrides in `applyNonceToDynamicScripts`; exported for unit tests.
 */
export const applyNonceIfAllowedToDynamicScriptNode = (
  node: Node | string,
  nonce: string,
): void => {
  if (
    typeof node === 'string' ||
    !(node instanceof HTMLScriptElement) ||
    node.hasAttribute('nonce')
  ) {
    return;
  }
  const scriptSrc = node.getAttribute('src') || undefined;
  if (isScriptSrcAllowedForCspNonce(scriptSrc)) {
    node.setAttribute('nonce', nonce);
  }
};

type ElementAppendFn = typeof Element.prototype.append;

const cspPatchedAppendFunctions = new WeakSet<ElementAppendFn>();

function patchElementAppendForCspNonce(element: HTMLElement | null, nonce: string): void {
  if (!element || typeof element.append !== 'function') {
    return;
  }
  const currentAppend = element.append;
  if (cspPatchedAppendFunctions.has(currentAppend)) {
    return;
  }
  const originalBound = currentAppend.bind(element);
  const wrapped = ((...nodes: (Node | string)[]) => {
    nodes.forEach(node => applyNonceIfAllowedToDynamicScriptNode(node, nonce));
    return originalBound(...nodes);
  }) as typeof element.append;
  cspPatchedAppendFunctions.add(wrapped);
  element.append = wrapped;
}

export const applyNonceToDynamicScripts = (nonce?: string) => {
  // Early return if no nonce provided
  if (!nonce) return;

  // Guard against multiple calls to ensure idempotency
  if (areCSPOverridesApplied) {
    return;
  }

  // Mark as applied to prevent future calls
  areCSPOverridesApplied = true;
  lastAppliedCspNonceForPatches = nonce;

  /**
   * Override Node.prototype.appendChild to catch scripts added to any DOM node.
   *
   * This is a comprehensive approach that catches scripts added via appendChild
   * regardless of which node they're being added to. This covers most dynamic
   * script insertion methods used by frameworks and libraries.
   */
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function patchNodeAppendChild(this: Node, child: Node) {
    applyNonceIfAllowedToDynamicScriptNode(child, nonce);
    return originalAppendChild.call(this, child);
  } as typeof Node.prototype.appendChild;

  /**
   * Override Node.prototype.insertBefore to catch scripts inserted before other elements.
   *
   * This covers scripts added via insertBefore, which is another common method
   * for dynamically inserting script elements into the DOM.
   */
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchNodeInsertBefore(
    this: Node,
    newNode: Node,
    referenceNode: Node | null,
  ) {
    applyNonceIfAllowedToDynamicScriptNode(newNode, nonce);
    return originalInsertBefore.call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;

  /**
   * Override document.head.appendChild / append when head exists (script may run before head in edge cases).
   */
  if (document.head) {
    const originalHeadAppendChild = document.head.appendChild;
    document.head.appendChild = function patchHeadAppendChild(child: Node) {
      applyNonceIfAllowedToDynamicScriptNode(child, nonce);
      return originalHeadAppendChild.call(document.head, child);
    } as typeof document.head.appendChild;

    patchElementAppendForCspNonce(document.head, nonce);
  }

  /**
   * document.body may be null when this runs from a synchronous script in head.
   * Node.prototype hooks still apply; defer body-specific overrides until body exists.
   */
  let bodyDocumentOverridesApplied = false;
  const patchBodyDocumentTargets = () => {
    if (!document.body || bodyDocumentOverridesApplied) {
      return;
    }
    bodyDocumentOverridesApplied = true;

    const originalBodyAppendChild = document.body.appendChild;
    document.body.appendChild = function patchBodyAppendChild(child: Node) {
      applyNonceIfAllowedToDynamicScriptNode(child, nonce);
      return originalBodyAppendChild.call(document.body, child);
    } as typeof document.body.appendChild;

    patchElementAppendForCspNonce(document.body, nonce);
  };

  if (document.body) {
    patchBodyDocumentTargets();
  } else {
    document.addEventListener('DOMContentLoaded', patchBodyDocumentTargets, { once: true });
  }

  // Webpack chunk loads inject <script> via appendChild/insertBefore (above). Do not patch
  // __webpack_require__.e with setTimeout — CSP is enforced before that runs.
};

/**
 * Resolves CSP nonce from the host embed when possible, then falls back to `getNonce(null)`
 * (`#kustomer-core-main`, `document.currentScript`, re-query `[data-kustomer-api-key]`).
 *
 * The explicit embed path must not short-circuit when the matched script has no nonce — otherwise
 * strict-CSP bundles that carry the nonce only on the executing tag (e.g. core-main) never get hooks.
 */
function tryApplyCspNonceFromDom(): void {
  if (areCSPOverridesApplied) {
    return;
  }
  const scriptEmbedForNonce = document.querySelector(
    '[data-kustomer-api-key]',
  ) as HTMLScriptElement | null;
  let nonce: string | undefined;
  if (scriptEmbedForNonce != null) {
    nonce = getNonce(scriptEmbedForNonce);
  }
  if (!nonce) {
    nonce = getNonce(null);
  }
  if (nonce) {
    applyNonceToDynamicScripts(nonce);
  }
}

/**
 * Re-patch `document.head` / `document.body` `append` after polyfills if `append`
 * was missing during `applyNonceToDynamicScripts()` (e.g. IE + core-js).
 * If hooks were never applied, attempts the same embed + `getNonce(null)` resolution as early init
 * (e.g. `#kustomer-core-main` after polyfills) before patching.
 * Safe to call multiple times; no-ops if nothing to patch.
 */
export const ensureCspNonceAppendPatchesAfterPolyfills = (): void => {
  tryApplyCspNonceFromDom();
  const nonce = lastAppliedCspNonceForPatches;
  if (!nonce || !areCSPOverridesApplied) {
    return;
  }
  const run = () => {
    if (document.head) {
      patchElementAppendForCspNonce(document.head, nonce);
    }
    if (document.body) {
      patchElementAppendForCspNonce(document.body, nonce);
    }
  };
  run();
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  }
};

/**
 * Awaits `installPolyfills()`, runs `ensureCspNonceAppendPatchesAfterPolyfills()`, then `callback`.
 * Use for core_main, widget_main, and widget_api boot so post-polyfill CSP `append` patching stays consistent.
 *
 * Returns a promise so callers or tests can observe failures from the post-polyfill steps.
 * If `installPolyfills` rejects, or **resolves** with a string (IE path: dynamic `import` failure is turned
 * into a resolved error message in `polyfills/helpers`), the callback is not run and the returned promise rejects.
 */
export const runAfterPolyfills = (callback: () => void): Promise<void> =>
  installPolyfills().then((result) => {
    if (typeof result === 'string') {
      throw new Error(result);
    }
    ensureCspNonceAppendPatchesAfterPolyfills();
    callback();
  });

/**
 * Reads CSP nonce from the DOM (embed + fallbacks) and calls `applyNonceToDynamicScripts`.
 * Invoke synchronously at module top **before** `installPolyfills()` on core_main, widget_main,
 * and widget_api so IE polyfill `import()` and webpack chunk `<script>` tags hit patched DOM APIs.
 */
export const initializeDynamicScriptNonceEarly = (): void => {
  tryApplyCspNonceFromDom();
};

/**
 * Shared SDK entry boot: {@link initializeDynamicScriptNonceEarly} runs synchronously first so CSP nonce
 * DOM hooks install before `installPolyfills()` and webpack chunk `<script>` injection; then
 * {@link runAfterPolyfills} runs post-polyfill `append` patching and invokes `afterPolyfills`.
 *
 * Attaches a rejection handler so fire-and-forget entrypoints (core_main, widget_api, widget_main) do not
 * surface unhandled promise rejections when polyfills fail; the returned promise still rejects for tests.
 */
export const bootstrapEntryPoint = (afterPolyfills: () => void): Promise<void> => {
  initializeDynamicScriptNonceEarly();
  const bootPromise = runAfterPolyfills(afterPolyfills);
  bootPromise.catch((error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('Kustomer: failed to initialize after polyfills', error);
  });
  return bootPromise;
};

// eslint-disable-next-line import/prefer-default-export
export const setupIframeForApi = (
  iframeId: string,
  iframeStyles: string,
  content: string,
  iframeTitle: string,
  nonce?: string
) => {
  const iframe = document.createElement('iframe');

  iframe.setAttribute('id', iframeId);
  iframe.setAttribute('title', iframeTitle);
  iframe.setAttribute('aria-label', iframeTitle);
  iframe.setAttribute('role', 'application');
  iframe.setAttribute('tabindex', '0');
  if (nonce) {
    iframe.setAttribute('nonce', nonce);
  }

  iframe.style.cssText = iframeStyles;

  appendElementToDocument(iframe, () => {
    setIframeSrcDoc(iframe, content);
  });
};

export const getIframeForWidgetMain = (): HTMLIFrameElement | undefined => {
  const iframe = document.getElementById(
    'kustomer-ui-sdk-iframe',
  ) as HTMLIFrameElement;

  if (!iframe) return undefined;

  return iframe;
};

export const getIframeForCoreApi = () => {
  const { frameElement } = window;
  const iframe = (
    frameElement?.nodeName === 'IFRAME'
      ? frameElement
      : document.getElementById('kustomer-core-sdk-iframe')
  ) as HTMLIFrameElement | undefined;

  if (iframe?.nodeName !== 'IFRAME') return undefined;

  return iframe;
};

export const setTrackingTokenInLocalStorage = (token) => {
  try {
    localStorage.setItem('kustomer-chat-tracking-token', token);
  } catch {
    throw new Error('Setting tracking token in local storage');
  }
};

export const getTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.getItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Getting tracking token from local storage');
  }
};

export const removeTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.removeItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Removing tracking token from local storage');
  }
};

/// define the key for pollin ginterval local storage
const POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY =
  'kustomer-initial-chat-polling-interval-ms';

/**
 * Removes the polling interval duration (in milliseconds) from local storage.
 * This is used to clear the stored polling interval setting.
 *
 * @throws {Error} If an error occurs while removing the polling interval.
 */
export const removePollingIntervalMsFromLocalStorage = () => {
  try {
    return localStorage.removeItem(POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY);
  } catch {
    return console.warn(
      'Error removing polling interval MS from local storage',
    );
  }
};

/**
 * Retrieves the polling interval duration (in milliseconds) from local storage.
 * If the value is not found or an error occurs, it returns the default polling interval.
 *
 * @returns {number} The polling interval in milliseconds, either from local storage or the default setting.
 */
export const getPollingIntervalMsFromLocalStorage = () => {
  try {
    const pollingIntervalMsString = localStorage.getItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
    );
    if (pollingIntervalMsString === null) return MESSAGE_POLL_SETTINGS.interval;
    return parseInt(pollingIntervalMsString, 10);
  } catch {
    // no matter what, return a value - in this case, default to previous behavior
    removePollingIntervalMsFromLocalStorage(); // remove, cleanup
    return MESSAGE_POLL_SETTINGS.interval;
  }
};

/**
 * Stores the polling interval duration (in milliseconds) in local storage.
 * This value determines the interval at which polling operations should occur.
 *
 * @param {string} pollingIntervalMsString - The polling interval duration in milliseconds, stored as a string.
 * @throws {Error} If an error occurs while setting the polling interval.
 */
export const setPollingIntervalMsFromLocalStorage = (
  pollingIntervalMsString: string,
) => {
  try {
    return localStorage.setItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
      pollingIntervalMsString,
    );
  } catch {
    return console.warn('Error setting polling interval MS in local storage');
  }
};

interface NavigatorWithStandalone extends Navigator {
  standalone: boolean;
}

/**
 * Currently only needed as a check for iOS where we are seeing sentry errors caused by embedded views
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator#navigator.standalone
 */
export const isInAppBrowser = () => {
  // this is something of a hack to get around TypeScript for a Safari/WebKit-only
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  if (!ios) return false;

  // If the device is considered "standalone", then we are in an embedded view
  return (
    'standalone' in window.navigator &&
    (window.navigator as NavigatorWithStandalone).standalone
  );
};

// In case the parent window is of a different origin
export function getSafeParentContext(windowParam: Window): Window | undefined {
  const win = windowParam ?? window;
  try {
    // Check if parent exists and has a location object accessible (won't throw cross-origin)
    if (win.parent && win.parent.location && win.parent.location.href) {
      return win.parent;
    }
  } catch (e) {
    return undefined;
  }

  // No parent or parent is the same window
  return undefined;
}
